import React, { useMemo } from 'react';

import { cn } from '@/lib/utils';

import { useLivePnL } from '../hooks/useLivePnL';
import { formatPnl } from '../hooks/useTerminalRows';
import {
    selectChartedLivePrice,
    selectLivePositionFor,
    useTradingStore,
} from '../store';
import { exchangePnlPercent } from '../utils/pnl';

interface PnlFigureProps {
    value: number;
    percent: number;
    /** Value over percent instead of side by side — for narrow rows. */
    stacked?: boolean;
}

export const PnlFigure: React.FC<PnlFigureProps> = ({
    value,
    percent,
    stacked,
}) => {
    const tone = value >= 0 ? 'text-success' : 'text-destructive';
    const sign = value >= 0 ? '+' : '';

    if (stacked) {
        return (
            <span className={cn('flex flex-col items-end leading-tight', tone)}>
                <span className="font-bold">{formatPnl(value)}</span>
                <span className="text-[10px] opacity-80">
                    {sign}
                    {percent.toFixed(2)}%
                </span>
            </span>
        );
    }

    return (
        <span className={cn('font-bold', tone)}>
            {formatPnl(value)} ({sign}
            {percent.toFixed(2)}%)
        </span>
    );
};

/**
 * Live PnL cell for the CHARTED market — a leaf so ~15Hz price ticks re-render
 * one <span>, not the whole positions table. Reads the price from the store
 * directly, so its hosts take no per-tick prop and can stay memoized.
 */
export const ChartedPnlCell: React.FC<{ stacked?: boolean }> = ({
    stacked,
}) => {
    const currentValue = useTradingStore((s) => s.currentValue);
    const { livePnLValue, livePnLPercent } = useLivePnL(currentValue);
    return (
        <PnlFigure
            value={livePnLValue}
            percent={livePnLPercent}
            stacked={stacked}
        />
    );
};

/**
 * Live PnL cell for a market that is NOT on the chart.
 *
 * These rows used to render "—", which reads as "this position has no PnL" when
 * it actually meant "nobody fetched it". Every held market's figures now arrive
 * from the bulk position read, so each row can show its own.
 *
 * Deliberately NOT extrapolated: the anchored delta needs a live price for the
 * market, and this client only streams the charted one. The exchange's own value
 * is shown verbatim — a few seconds stale between polls rather than wrong.
 */
export const OffChartPnlCell: React.FC<{
    coin: string;
    stacked?: boolean;
}> = ({ coin, stacked }) => {
    const live = useTradingStore(
        useMemo(() => selectLivePositionFor(coin), [coin]),
    );
    if (live === null || live.unrealizedPnl === null) {
        return <span className="text-muted-foreground">—</span>;
    }
    return (
        <PnlFigure
            value={live.unrealizedPnl}
            percent={exchangePnlPercent(live) ?? 0}
            stacked={stacked}
        />
    );
};

/**
 * How far the market still has to travel to fill a resting limit order.
 *
 * It takes the PnL column because a resting order HAS no PnL — nothing is at
 * risk until it fills — and "how close is it" is the only live number it has.
 * Muted and unsigned on purpose: a distance is neither a profit nor a loss, so
 * colouring it lime or red would read as one.
 *
 * Only the charted market has a live price on this client, so an off-chart
 * order shows a dash rather than a number derived from the wrong market.
 */
export const RestingDistanceCell: React.FC<{
    coin: string;
    limitPrice: number;
    stacked?: boolean;
}> = ({ coin, limitPrice }) => {
    const chartedPrice = useTradingStore(selectChartedLivePrice);
    const selectedCoin = useTradingStore((s) => s.selectedCoin);
    const isCharted =
        selectedCoin !== null && selectedCoin === coin.toUpperCase();

    if (!isCharted || chartedPrice === null || !(limitPrice > 0)) {
        return <span className="text-muted-foreground">—</span>;
    }
    const awayPct = (Math.abs(limitPrice - chartedPrice) / chartedPrice) * 100;
    return (
        <span className="text-muted-foreground">
            {awayPct.toFixed(2)}% away
        </span>
    );
};
